import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminScreen, LoadOrError, adminErrorText, useResource } from './Overview';
import { Alert, Button, Card, EmptyState, Icon, Modal, Spinner, StatusPill, useToast } from '../../components/ui';
import { api } from '../../lib/api';
import { dateTimePt } from '../../lib/format';
import { usePageMeta } from '../../lib/seo';
import { tr } from '../../lib/i18n';

interface FlaggedReviewRow {
  id: string;
  rating: number;
  comment: string | null;
  report: string | null;
  created_at: string;
  businessName: string;
  professionalName: string;
  /** Não devolvido por GET /api/admin/reports/flagged-reviews; tratado se a rota passar a incluir. */
  owner_response?: string | null;
}

interface ReviewsPayload {
  reviews: FlaggedReviewRow[];
}

function reportReason(raw: string | null): { reason: string; detail: string } {
  if (!raw) return { reason: 'Denúncia sem motivo registrado', detail: '' };
  let parsed: unknown = null;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { reason: 'Denúncia', detail: String(raw) };
  }
  if (parsed && typeof parsed === 'object') {
    const obj = parsed as Record<string, unknown>;
    const reason = typeof obj.reason === 'string' ? obj.reason : typeof obj.type === 'string' ? obj.type : 'Denúncia';
    const detail = typeof obj.detail === 'string'
      ? obj.detail
      : typeof obj.message === 'string'
        ? obj.message
        : JSON.stringify(obj);
    return { reason, detail };
  }
  return { reason: 'Denúncia', detail: String(parsed) };
}

export function AdminReviewsPage(): JSX.Element {
  usePageMeta(tr('Avaliações denunciadas — administração'), tr('Moderação de avaliações reportadas.'), '/admin/avaliacoes');

  const toast = useToast();
  const resource = useResource<ReviewsPayload>(() => api.get<ReviewsPayload>('/api/admin/reports/flagged-reviews'));
  const [pending, setPending] = useState<{ review: FlaggedReviewRow; action: 'publish' | 'hide' } | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const reviews = resource.data?.reviews ?? [];

  async function moderate(): Promise<void> {
    if (!pending) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.post<{ ok: boolean }>(`/api/admin/reviews/${pending.review.id}/moderate`, { action: pending.action });
      toast(
        pending.action === 'publish'
          ? 'Avaliação publicada e visível no perfil público.'
          : 'Avaliação escondida: some do perfil público, mas o registro é preservado.',
        'success'
      );
      setPending(null);
      await resource.reload();
    } catch (e) {
      setActionError(adminErrorText(e));
      toast(adminErrorText(e), 'danger');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AdminScreen
      title={tr("Avaliações denunciadas")}
      description={tr("Avaliações marcadas como sinalizadas, com nota, comentário e motivo da denúncia. Publique para manter visível ou esconda para retirar do perfil público.")}
    >
      <LoadOrError loading={resource.loading} error={resource.error} />

      {!resource.loading && reviews.length === 0 && !resource.error ? (
        <EmptyState
          title={tr("Nada pendente de moderação")}
          description={tr("Nenhuma avaliação está com situação “flagged” no momento. Avaliações escondidas voltam para /admin/auditoria quando moderadas.")}
          action={<Button variant="secondary" disabled={resource.loading} onClick={() => void resource.reload()}>{tr("Recarregar")}</Button>}
        />
      ) : null}

      {reviews.length > 0 ? (
        <p className="nl-hint mb-3">
          {reviews.length} {reviews.length === 1 ? 'avaliação aguardando decisão' : 'avaliações aguardando decisão'} {tr("· a rota retorna no máximo 100 registros.")}
        </p>
      ) : null}

      <div className="grid gap-3">
        {reviews.map((review) => {
          const { reason, detail } = reportReason(review.report);
          return (
            <Card key={review.id} as="article">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span aria-hidden="true" className="flex">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <span key={star} className={star <= review.rating ? 'text-sand' : 'text-line'}>
                          <Icon name="star" size={16} />
                        </span>
                      ))}
                    </span>
                    <span className="font-semibold">{review.rating} {tr("de 5 estrelas")}</span>
                    <StatusPill tone="danger">{tr("Denunciada")}</StatusPill>
                    <span className="nl-hint">{dateTimePt(review.created_at)}</span>
                  </div>

                  <blockquote className="mt-3 whitespace-pre-wrap rounded-md bg-surface px-3 py-2">
                    {review.comment ? review.comment : <span className="nl-hint">{tr("O cliente não escreveu comentário.")}</span>}
                  </blockquote>

                  <p className="mt-3 text-sm">
                    <strong>{tr("Motivo da denúncia:")}</strong> {reason}
                    {detail ? <span className="nl-hint block break-words">{detail}</span> : null}
                  </p>

                  <p className="nl-hint mt-2">
                    {review.businessName} {tr("· profissional")} {review.professionalName}
                  </p>

                  {review.owner_response ? (
                    <p className="mt-2 rounded-md border border-line px-3 py-2 text-sm">
                      <strong>{tr("Resposta do proprietário:")}</strong> {review.owner_response}
                    </p>
                  ) : null}
                </div>

                <div className="flex shrink-0 flex-col gap-2">
                  <Button
                    variant="secondary"
                    disabled={busy}
                    onClick={() => {
                      setActionError(null);
                      setPending({ review, action: 'publish' });
                    }}
                  >
                    {tr("Manter / publicar")}
                  </Button>
                  <Button
                    variant="danger"
                    disabled={busy}
                    onClick={() => {
                      setActionError(null);
                      setPending({ review, action: 'hide' });
                    }}
                  >
                    {tr("Esconder")}
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {actionError ? <div className="mt-4"><Alert tone="danger" title={tr("Ação não concluída")}>{actionError}</Alert></div> : null}

      <Alert tone="info" title={tr("Limites reais desta tela")}>
        <span className="block">
          {tr("A listagem devolve apenas denúncia, nota, comentário, estabelecimento e profissional. A coluna")}{' '}
          <code>{tr("owner_response")}</code> {tr("existe no banco, mas")} <code>{tr("GET /api/admin/reports/flagged-reviews")}</code> {tr("não a seleciona — a resposta do proprietário só aparece se a rota passar a retorná-la.")}
        </span>
        <span className="mt-1 block">
          {tr("Também não há rota que marque uma avaliação como denunciada nem que responda publicamente a uma avaliação; não existe exclusão de avaliação — apenas publicar ou esconder. Veja o histórico completo em")}{' '}
          <Link className="font-semibold underline" to="/admin/auditoria">{tr("Auditoria")}</Link>.
        </span>
      </Alert>

      <Modal
        open={pending !== null}
        onClose={() => (busy ? undefined : setPending(null))}
        title={pending?.action === 'publish' ? 'Publicar avaliação denunciada' : 'Esconder avaliação denunciada'}
        footer={
          <>
            <Button variant="ghost" disabled={busy} onClick={() => setPending(null)}>{tr("Voltar")}</Button>
            <Button
              variant={pending?.action === 'hide' ? 'danger' : 'primary'}
              loading={busy}
              disabled={busy}
              onClick={() => void moderate()}
            >
              {pending?.action === 'hide' ? 'Esconder avaliação' : 'Publicar avaliação'}
            </Button>
          </>
        }
      >
        {pending ? (
          <div className="space-y-3">
            <Alert tone={pending.action === 'hide' ? 'warning' : 'success'} title={tr("Consequência")}>
              {pending.action === 'hide'
                ? 'A avaliação sai do ar (situação “hidden”): some da média e do perfil público do profissional, mas o registro continua no banco e pode ser republicado depois.'
                : 'A avaliação volta a ser publicada (situação “published”), volta a contar na média do profissional e fica visível para todos os clientes.'}{' '}
              {tr("A decisão é registrada em Auditoria (")}<code>{tr("admin.review_")}{pending.action}</code>{tr(") e não notifica o cliente nem o estabelecimento.")}
            </Alert>
            <p className="text-sm">
              {tr("Nota")} {pending.review.rating} {tr("de 5 em")} {pending.review.businessName} ({pending.review.professionalName}),
              {' '}{tr("denunciada como “")}{reportReason(pending.review.report).reason}”.
            </p>
            {busy ? <Spinner label={tr("Aplicando")} /> : null}
          </div>
        ) : null}
      </Modal>
    </AdminScreen>
  );
}
